import React, { createContext, useState, useCallback, useMemo, useEffect } from 'react';

export const AuthContext = createContext(null);

const LS_TOKEN = 'token';
const LS_USER = 'user';
const LS_REFRESH_TOKEN = 'refresh_token';
const SS_TOKEN = 'token_session';
const SS_USER = 'user_session';
const SS_REFRESH_TOKEN = 'refresh_token_session';
const ADMIN_TOKEN = 'court_portal_admin_token';
const ADMIN_USER = 'court_portal_admin_user';

function getStorage(remember) {
  return remember ? localStorage : sessionStorage;
}

function loadFromBoth() {
  let token = localStorage.getItem(LS_TOKEN);
  let userStr = localStorage.getItem(LS_USER);
  if (!token || !userStr) {
    token = sessionStorage.getItem(SS_TOKEN);
    userStr = sessionStorage.getItem(SS_USER);
  }
  if (token && userStr) {
    try {
      return { token, user: JSON.parse(userStr) };
    } catch {
      localStorage.removeItem(LS_TOKEN);
      localStorage.removeItem(LS_USER);
      localStorage.removeItem(LS_REFRESH_TOKEN);
      sessionStorage.removeItem(SS_TOKEN);
      sessionStorage.removeItem(SS_USER);
      sessionStorage.removeItem(SS_REFRESH_TOKEN);
    }
  }
  return null;
}

function clearBoth() {
  localStorage.removeItem(LS_TOKEN);
  localStorage.removeItem(LS_USER);
  localStorage.removeItem(LS_REFRESH_TOKEN);
  localStorage.removeItem(ADMIN_TOKEN);
  localStorage.removeItem(ADMIN_USER);
  sessionStorage.removeItem(SS_TOKEN);
  sessionStorage.removeItem(SS_USER);
  sessionStorage.removeItem(SS_REFRESH_TOKEN);
  sessionStorage.removeItem(ADMIN_TOKEN);
  sessionStorage.removeItem(ADMIN_USER);
}

function getActiveStorage() {
  return localStorage.getItem(LS_TOKEN) ? localStorage : sessionStorage;
}

function getSessionKeys(storage) {
  return storage === localStorage
    ? { tokenKey: LS_TOKEN, userKey: LS_USER }
    : { tokenKey: SS_TOKEN, userKey: SS_USER };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const saved = loadFromBoth();
    if (saved) {
      setUser(saved.user);
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (
    token,
    userData,
    rememberMe = true,
    refreshToken = null
  ) => {
    const storage = getStorage(rememberMe);
    const tokenKey = rememberMe ? LS_TOKEN : SS_TOKEN;
    const userKey = rememberMe ? LS_USER : SS_USER;
    const refreshTokenKey = rememberMe ? LS_REFRESH_TOKEN : SS_REFRESH_TOKEN;
    clearBoth();
    storage.setItem(tokenKey, token);
    storage.setItem(userKey, JSON.stringify(userData));
    if (refreshToken) storage.setItem(refreshTokenKey, refreshToken);
    setUser(userData);
  }, []);

  const loginAsVenueOwner = useCallback((token, userData) => {
    const storage = getActiveStorage();
    const { tokenKey, userKey } = getSessionKeys(storage);
    const currentToken = storage.getItem(tokenKey);
    const currentUser = storage.getItem(userKey);

    if (!currentToken || !currentUser) {
      throw new Error('The administrator session has expired. Please sign in again.');
    }

    if (!storage.getItem(ADMIN_TOKEN)) {
      storage.setItem(ADMIN_TOKEN, currentToken);
      storage.setItem(ADMIN_USER, currentUser);
    }
    storage.setItem(tokenKey, token);
    storage.setItem(userKey, JSON.stringify({ ...userData, isAdminDelegatedSession: true }));
    setUser({ ...userData, isAdminDelegatedSession: true });
  }, []);

  const returnToOwnerSelection = useCallback(() => {
    const storage = localStorage.getItem(ADMIN_TOKEN) ? localStorage : sessionStorage;
    const adminToken = storage.getItem(ADMIN_TOKEN);
    const adminUser = storage.getItem(ADMIN_USER);
    if (!adminToken || !adminUser) return false;

    const { tokenKey, userKey } = getSessionKeys(storage);
    storage.setItem(tokenKey, adminToken);
    storage.setItem(userKey, adminUser);
    const parsedAdmin = JSON.parse(adminUser);
    setUser(parsedAdmin);
    return true;
  }, []);

  const logout = useCallback(() => {
    clearBoth();
    setUser(null);
  }, []);

  const isAuthenticated = useMemo(() => !!user, [user]);
  const isAdminDelegatedSession = useMemo(
    () => Boolean(user?.isAdminDelegatedSession),
    [user]
  );

  const value = useMemo(
    () => ({
      user,
      login,
      loginAsVenueOwner,
      returnToOwnerSelection,
      logout,
      isAuthenticated,
      isAdminDelegatedSession,
      loading,
    }),
    [user, login, loginAsVenueOwner, returnToOwnerSelection, logout, isAuthenticated, isAdminDelegatedSession, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
